import { useSnackbar } from "@sagecentral/core-ui";
import { ValidationSummary } from "components/shared";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { method } from "lodash";
import { IProductMaster } from "models/product-master";
import { IWorkItemProductMaster } from "models/work-items";
import IWorkItemProductMasterTitleInfo from "models/work-items/IWorkItemProductMasterTitleInfo";
import { useEffect, useReducer, useState } from "react";

type SearchBookIdSignature = (workitemId: number, bookId: number) => Promise<IProductMaster | null>;
type RefreshBookIdSignature = (workitemId: number, bookId: number) => Promise<boolean | null>;
type UnlinkBookIdSignature = (workitemId: number, bookId: number) => Promise<boolean | null>;
type LinkBookIdSignature = (workitemId: number, bookId: number, title: string) => Promise<boolean | null>;

type UseWorkItemProductMasterResult = [
    IProductMaster | null,
    boolean,
    SearchBookIdSignature,
    RefreshBookIdSignature,
    UnlinkBookIdSignature,
    LinkBookIdSignature,
    () => void, // refresh
    boolean
];

type UseWorkItemProductMasterSignature = (workItemId: number, draft: boolean) => UseWorkItemProductMasterResult;

const useWorkItemProductMaster: UseWorkItemProductMasterSignature = (workItemId, draft = false) => {
    const [ value, setValue ] = useState<IProductMaster | null>(null);
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ refreshState, refresh ] = useReducer(v => v + 1, 0);
    const [ isLinked, setIsLinked ] = useState<boolean>(false);
    const { displaySnackbar } = useSnackbar();

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const errorHandler = async (resp: Response) => {
        if (resp.status === 400) {
            setValue(null);

            const data = await resp.json();

            displaySnackbar('warning', 
                <ValidationSummary 
                    validationDetail={data}                
                />
            );

            return true;
        }

        return false;
    };

    useEffect(() => {        
        if (!user || !user.access_token) {
            return;
        }

        const controller = new AbortController();
        (async () => {
            setLoading(true);
            try {
                const resp = await fetcher(draft 
                    ? `/api/v3/drafts/workitems/${workItemId}/product-master/title` 
                    : `/api/v3/workitems/${workItemId}/product-master/title`, {
                        signal: controller.signal
                    }, async (resp: Response) => {
                        if (resp.status === 404) {
                            setValue(null);
                            return true;
                        }
    
                        return await errorHandler(resp);
                    });

                if (!resp.ok) {
                    setValue(null);
                }

                const data = await resp.json() as IWorkItemProductMaster;
                const result = await searchBookId(workItemId, data.bookId);
                
                if(result != null){
                    result.BookId = data.bookId;
                    result.SourceLink = data.sourceLink
                    setIsLinked(true);
                }

                setValue(result);
            } catch {
                setValue(null);
            } finally {
                setLoading(false);
            }
        })();

        return () => {
            controller.abort();
        }
    }, [workItemId, user, refreshState]);

    const searchBookId: SearchBookIdSignature = async (workitemId: number, bookId: number) => {
        if (!user || !user.access_token) {
            return null;
        }
    
        const controller = new AbortController();
        
        try {
            setLoading(true);
    
            const resp = await fetcher(draft 
                ? `/api/v3/drafts/workitems/${workitemId}/product-master/${bookId}`
                : `/api/v3/workitems/${workitemId}/product-master/${bookId}`, {
                    signal: controller.signal
                }, async (resp: Response) => {
                    if (!await errorHandler(resp)) {
                        setValue(null);
                        displaySnackbar('warning', 'Unexpected error occurred.');
                    }

                    return true;
                });
         
            if (!resp.ok) {
                return null;
            }
            
            const data = await resp.json();

            const result = data[0] as IProductMaster;
            result.BookId = bookId;
            return result;
        } catch {
            setValue(null);
            return null;
        } finally {
            setLoading(false);
        }
    };


    const refreshBookId: RefreshBookIdSignature = async (workitemId: number, bookId: number) => {
        if (!user || !user.access_token) {
            return null;
        }
    
        const controller = new AbortController();
        
        try {
            setLoading(true);            
    
            const resp = await fetcher(draft 
                ? `/api/v3/drafts/workitems/${workitemId}/product-master/${bookId}/refresh`
                : `/api/v3/workitems/${workitemId}/product-master/${bookId}/refresh`, {
                    signal: controller.signal,
                    method: 'PUT'
                }, errorHandler);
         
            if (!resp.ok) {
                return false;
            }

            displaySnackbar('success', 'Book ID in product master refreshed with this work item');

            return true;

        } catch {
            return false;
        } finally {
            setLoading(false);
            return true;
        }
    };
    
    const unlinkBookId: UnlinkBookIdSignature = async (workitemId: number, bookId: number) => {
        if (!user || !user.access_token) {
            return null;
        }
    
        const controller = new AbortController();
        
        try {
            setLoading(true);
    
            const resp = await fetcher(draft 
                ? `/api/v3/drafts/workitems/${workitemId}/product-master/${bookId}/unlink`
                : `/api/v3/workitems/${workitemId}/product-master/${bookId}/unlink`, {
                    signal: controller.signal,
                    method: 'PUT'
                }, errorHandler);
         
            if (!resp.ok) {
                return false;
            }
            
            setIsLinked(false);
            displaySnackbar('success', 'Book ID in product master unlinked from this work item');
           
            return await resp.json() as boolean;

        } catch {
            setValue(null);
            return false;
        } finally {
            setLoading(false);
            return true;
        }
    };

    const linkBookId: LinkBookIdSignature = async (workitemId: number, bookId: number, title: string) => {
        if (!user || !user.access_token) {
            return null;
        }
    
        const controller = new AbortController();
        
        try {
            setLoading(true);
            let url = draft 
                ? `/api/v3/drafts/workitems/${workitemId}/product-master/${bookId}/link`
                : `/api/v3/workitems/${workitemId}/product-master/${bookId}/link`;
            if (title) {
                url += `?title=${title}`;
            }

            const resp = await fetcher(url, {
                    signal: controller.signal,
                    method: 'PUT'
                }, errorHandler);

            if (!resp.ok) {
                return false;
            }
            
            setIsLinked(true);
            displaySnackbar('success', 'Book ID in product master linked to this work item');

            return await resp.json() as boolean;

        } catch {
            setValue(null);
            return false;
        } finally {
            return true;
        }
    };

    return [
        value,
        isLinked,
        searchBookId,
        refreshBookId,
        unlinkBookId,
        linkBookId,
        () => refresh(),
        loading
    ];
};

export default useWorkItemProductMaster;